import type { Contact } from './types';

export function getMetadataString(
	contact: Contact,
	...keys: string[]
): string | null {
	for (const key of keys) {
		const value = contact.metadata?.[key];
		if (typeof value === 'string' && value.trim()) {
			return value.trim();
		}
	}
	return null;
}

export function getDisplayName(contact: Contact): string {
	return (
		contact.name?.trim() ||
		getMetadataString(contact, 'name') ||
		getEmail(contact) ||
		getPhone(contact) ||
		(getInstagramHandle(contact) ? `@${getInstagramHandle(contact)}` : null) ||
		'Unknown'
	);
}

export function getInitials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	const first = parts[0]?.charAt(0).toUpperCase() ?? '?';
	const last =
		parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : '';
	return last ? `${first}${last}` : first;
}

export function getEmail(contact: Contact): string | null {
	return (
		contact.email?.trim() ||
		contact.identifiers?.find((identifier) => identifier.kind === 'email')
			?.normalized_value ||
		getMetadataString(contact, 'email', 'email_address')
	);
}

export function getPhone(contact: Contact): string | null {
	return (
		contact.phone?.trim() ||
		contact.identifiers?.find((identifier) => identifier.kind === 'phone')
			?.normalized_value ||
		getMetadataString(contact, 'phone', 'phone_number')
	);
}

export function getInstagramHandle(contact: Contact): string | null {
	const handle =
		contact.instagram_handle?.trim() ||
		contact.identifiers?.find(
			(identifier) =>
				identifier.kind === 'social_handle' &&
				identifier.provider === 'instagram',
		)?.normalized_value ||
		getMetadataString(contact, 'instagram_handle', 'instagram');
	return handle?.replace(/^@/, '') ?? null;
}

export function getLastSeen(contact: Contact): string | null {
	return (
		contact.last_seen ??
		getMetadataString(contact, 'last_seen', 'lastSeen') ??
		contact.updated_at ??
		contact.created_at ??
		null
	);
}

export function getEmailConsentStatus(contact: Contact): string {
	return contact.consent?.email?.status ?? 'unknown';
}

export function formatSource(source: string | null | undefined): string {
	if (!source) return 'Unknown';
	if (source.toLowerCase() === 'csv') return 'CSV';
	return source.charAt(0).toUpperCase() + source.slice(1);
}

export function getActiveSources(contact: Contact): string[] {
	const refs = contact.source_refs ?? [];
	const sources = refs
		.filter((ref) => ref.status !== 'deleted')
		.map((ref) => formatSource(ref.source));
	if (contact.source) {
		sources.unshift(formatSource(contact.source));
	}
	return [...new Set(sources)];
}

export function formatRelativeTime(isoDate: string | null | undefined): string {
	if (!isoDate) return 'Unknown';

	const date = new Date(isoDate);
	if (Number.isNaN(date.getTime())) return 'Unknown';

	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (diffDays === 0) {
		const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
		if (diffHours === 0) {
			const diffMinutes = Math.floor(diffMs / (1000 * 60));
			if (diffMinutes < 1) return 'Just now';
			return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
		}
		return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
	}

	if (diffDays === 1) return '1 day ago';
	if (diffDays < 7) return `${diffDays} days ago`;

	const diffWeeks = Math.floor(diffDays / 7);
	if (diffWeeks === 1) return '1 week ago';
	if (diffWeeks < 4) return `${diffWeeks} weeks ago`;

	const diffMonths = Math.floor(diffDays / 30);
	if (diffMonths === 1) return '1 month ago';
	if (diffMonths < 12) return `${diffMonths} months ago`;

	const diffYears = Math.floor(diffDays / 365);
	return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}
